import { Component, inject, Input, OnChanges } from '@angular/core';
import { SelectItem } from '../../../shared/components/select-menu/select-menu.component';
import { BrandPerformanceByDevice, DevicePerformance, DeviceType, MyCompetitionPerformanceByDeviceWidgetResponse } from '../types';
import { UtilService } from '../../../services/util.service';
import { HighchartsChartModule } from 'highcharts-angular';
import Highcharts from 'highcharts';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { NoWidgetDataComponent } from '../../my-brand-screen/no-widget-data/no-widget-data.component';
import { NgIf } from '@angular/common';
import { PerformanceComparisonMetric } from '../../my-brand-screen/types';
import { TabsComponent } from '../../../shared/components/tabs/tabs.component';

enum TabType {
  DEVICE = 'Device',
  BRAND = 'Brand'
}

@Component({
  selector: 'app-performance-by-device',
  imports: [
    NgIf,
    HighchartsChartModule,
    LoaderComponent,
    NoWidgetDataComponent,
    TabsComponent
  ],
  templateUrl: './performance-by-device.component.html',
  styleUrl: './performance-by-device.component.scss'
})
export class PerformanceByDeviceComponent implements OnChanges {
  @Input() loading = false;
  @Input() data?: MyCompetitionPerformanceByDeviceWidgetResponse;
  @Input() metric?: SelectItem;
  @Input() brandColors?: Record<string, string>;

  utilService = inject(UtilService);

  public containerId = 'performance-by-device-chart-container';
  public selectedType = TabType.DEVICE;
  
  private devices = [DeviceType.DESKTOP, DeviceType.TABLET, DeviceType.MOBILE];
  private devicesColors = { 
    [DeviceType.DESKTOP]: '#8EC2D4',
    [DeviceType.MOBILE]: '#AD588E',
    [DeviceType.TABLET]: '#CDD1D7'
  };
  private textStyle = {
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '18px',
    color: '#4E5258'
  };

  chartInstance: Highcharts.Chart | undefined;
  chartOptions: Highcharts.Options = {
    chart: {
      type: 'column',
      height: 470,
      animation: false
    },
    title: {
      text: undefined
    },
    credits: {
      enabled: false
    },
    tooltip: {
      enabled: false
    },
    xAxis: {
      title: {
        text: null
      },
      labels: {
        style: this.textStyle
      }
    },
    yAxis: {
      title: {
        text: null
      },
    },
    legend: {
      title: {
        text: undefined
      },
      itemStyle: this.textStyle,
      align: 'left',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      margin: 24
    }
  };

  ngOnChanges() {
    this.setChartInstance();
    this.updateChartOptions();
  }

  private setChartInstance(): void {
    const hasChartData = this.hasChartData();

    // If there’s no data, destroy the chart if it exists
    if (!hasChartData) {
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = undefined;
      }
      return;
    }

    // If there’s data, create or update the chart
    if (this.chartInstance) {
      this.chartInstance.update(this.chartOptions, true, true);
    } else {
      this.chartInstance = Highcharts.chart(this.containerId, this.chartOptions);
    }
  }

  private hasChartData(): boolean {
    return !!this.data?.brands?.length;
  }

  private updateChartOptions(): void {
    if (!this.chartInstance || !this.data?.brands?.length || !this.metric) {
      return;
    }

    const getDisplayMetric = this.utilService.getDisplayMetric;
    const metric = this.metric.key as PerformanceComparisonMetric;
    const metricValues = this.extractMetricValues(this.data.brands, metric);
    const isFullRangeMetric =  metric === PerformanceComparisonMetric.ATTENTION || metric === PerformanceComparisonMetric.VIEWABILITY;
    const { min, max } = this.utilService.getMetricRange(metricValues, isFullRangeMetric);
    const categories = this.getCategories();
    const series = this.getSeries(this.data, metric);
    
    this.updateSeries(series);

    this.chartInstance.update({
      xAxis: {
        categories: categories
      },
      yAxis: {
        min: min,
        max: max,
        labels: {
          formatter: function (this: any) {
            return getDisplayMetric(this.value, metric, true);
          },
          style: this.textStyle
        }
      },
      plotOptions: {
        column: {
          pointWidth: (series.length > 4 || categories.length > 4) ? undefined : 36,
        }
      }
    }, false);

    this.chartInstance.redraw();
  }

  private getCategories(): string[] {
    if (this.selectedType === TabType.BRAND) {
      return this.data?.brands.map(brand => brand.brandName) || [];
    } else {
      return this.devices.map(deviceType => this.utilService.capitalizeFirstLetter(deviceType));
    }
  }

  private getSeries(data: MyCompetitionPerformanceByDeviceWidgetResponse, metric: PerformanceComparisonMetric): Highcharts.SeriesOptionsType[] {

    const getMetricValue = (devices: DevicePerformance[], deviceType: DeviceType): number => {
      const data = devices.find(d => d.deviceType === deviceType);
      // @ts-ignore
      return data ? data[metric] : 0;
    };

    if (this.selectedType === TabType.BRAND) {
      return this.devices.map(deviceType => ({
        type: 'column',
        name: this.utilService.capitalizeFirstLetter(deviceType),
        data: this.data?.brands.map(brand => getMetricValue(brand.devices, deviceType)),
        color: this.devicesColors[deviceType],
        animation: false
      }));
    } else {
      return data.brands.map(brand => ({
        type: 'column',
        name: brand.brandName,
        data: this.devices.map(deviceType => getMetricValue(brand.devices, deviceType)),
        color: this.brandColors?.[brand.brandKey],
        animation: false
      }));
    }
  }

  private updateSeries(series: Highcharts.SeriesOptionsType[]): void {
    if (!this.chartInstance) {
      return;
    }
    // Remove all existing series
    while (this.chartInstance.series.length) {
      this.chartInstance.series[this.chartInstance.series.length - 1].remove(false, false);
    }
    // Add new series
    series.forEach((s) => {
      this.chartInstance?.addSeries(s, false, false);
    });
  }

  private extractMetricValues(brands: BrandPerformanceByDevice[], metric: PerformanceComparisonMetric): number[] {
    return brands.flatMap(brand => 
      // @ts-ignore
      brand.devices.map(device => device[metric])
    ).filter(value => !isNaN(value) && value !== null) as number[];
  }

  public switchTab(tab: string) {
    this.loading = true;
    setTimeout(() => {
      this.loading = false;
      this.selectedType = tab === TabType.DEVICE ? TabType.DEVICE : TabType.BRAND;
      this.updateChartOptions();
    }, 400);
  }
}
